import {checkObjectWithKeyword, shiftToFirstBy} from '@shared/helpers/array'
import {eq, isEmpty} from '@shared/helpers/lang'

import {enableRechargePoint} from '@web/actions/pointPayment'
import {selectSecondPayMethod} from '@web/actions/priorityPaymentMethod'
import {restoreOrderSheetDataByStorage} from '@web/actions/restoreState'
import {
    BillingPayMethodsKey,
    isBillingPayMethods,
    PAYMENT_PRIORITY,
    STORAGE_RECOVERY_TYPE,
    SubscribePayMethod,
} from '@web/core/constants'
import getStorage, {getRenewPaymentsStorage} from '@web/helpers/storage'
import {
    BankEasyPayView,
    CardEasyPay,
    PriorityPaymentMethodState,
    updateFirstPayMethod,
    updateSecondPayMethod,
} from '@web/slices/priorityPaymentMethod/slice'
import makeSlice from '@web/slices/priorityPaymentMethod/sliceMaker'

import {initPaymentMethodManagement} from '../../actions'
import {State} from '../../interfaces'

import {
    convertBankEasyPay,
    convertCardEasyPay,
    convertPaymentTabMethodToBillingPayMethod,
    convertPaymentTabMethodToPayMethod,
    convertToBillingPayMethodFrom,
    convertToPayMethodFrom,
    getSecondPaymentMethodByManagementPolicy,
    isAccountSuccessCode,
    shiftToFirstByChargedAccounts,
} from './utils'

const slice = makeSlice({
    extraReducers(builder) {
        builder
            .addCase(initPaymentMethodManagement.fulfilled, (state, {payload: {response}}) => {
                const {accountResult, cardResult, prevPaymentMethodInfo} = response

                const {regPgs} = prevPaymentMethodInfo
                const [primaryPayment, secondaryPayment] = regPgs

                const {pgcd: primaryPgcd, corpKey: primaryCorpKey} = primaryPayment ?? {}
                const {pgcd: secondaryPgcd, corpKey: secondaryCorpKey} = secondaryPayment ?? {}

                /** 1차 결제수단 */
                const nextBillingPayMethod = convertToBillingPayMethodFrom(primaryPgcd)
                const eqBillingPayMethod = eq(nextBillingPayMethod)
                state.selectedPayment.billingPayMethod = nextBillingPayMethod ?? null
                state.selectedPayment.selectedMethod = convertToPayMethodFrom(primaryPgcd)

                if (eqBillingPayMethod(BillingPayMethodsKey.CARD_EASY_PAY) && primaryCorpKey) {
                    state.selectedPayment.cardEasyPay.cardId = primaryCorpKey
                } else if (eqBillingPayMethod(BillingPayMethodsKey.RECHARGE_POINT) && primaryCorpKey) {
                    state.selectedPayment.chargeAccount.accountKey = primaryCorpKey
                    state.selectedSecondPayment.chargeAccount.accountKey = primaryCorpKey
                }

                /** 2차 결제수단 복구 */
                const nextSecondaryBillingPayMethod = convertToBillingPayMethodFrom(secondaryPgcd)
                const eqSecondaryBillingPayMethod = eq(nextSecondaryBillingPayMethod)
                state.selectedSecondPayment.billingPayMethod = nextSecondaryBillingPayMethod ?? null
                state.selectedSecondPayment.selectedMethod = convertToPayMethodFrom(secondaryPgcd)

                if (eqSecondaryBillingPayMethod(BillingPayMethodsKey.CARD_EASY_PAY) && secondaryCorpKey) {
                    state.selectedSecondPayment.cardEasyPay.cardId = secondaryCorpKey
                } else if (eqSecondaryBillingPayMethod(BillingPayMethodsKey.RECHARGE_POINT) && secondaryCorpKey) {
                    state.selectedPayment.chargeAccount.accountKey = secondaryCorpKey
                    state.selectedSecondPayment.chargeAccount.accountKey = secondaryCorpKey
                } else {
                    /** 2차 결제수단이 올바르지 않은 정보가 온 경우 */
                    state.selectedSecondPayment.billingPayMethod = null
                    state.selectedSecondPayment.selectedMethod = SubscribePayMethod.NONE
                }

                const storage = getStorage<State>('')

                /** 계좌 목록 초기화 */
                const {acctList, resultCode} = accountResult

                /** result code success case
                 * - 1000 (정상, 일부 계좌 점검중)
                 * - 3001 (등록된 계좌 없음)
                 * - 3106 (은행 전체점검) -> 디콘에서는 정상 처리
                 * */
                if (resultCode && isAccountSuccessCode(resultCode)) {
                    const validBankEasyPays =
                        acctList.filter(({regCmplTime}) => !isEmpty(regCmplTime)).map(convertBankEasyPay()) ?? []
                    const {
                        bankEasyPay: {accountKey: primaryBankKey},
                        chargeAccount: {accountKey: chargeKey},
                    } = state.selectedPayment
                    const {
                        bankEasyPay: {accountKey: secondaryBankKey},
                        chargeAccount: {accountKey: secondaryChargeKey},
                    } = state.selectedSecondPayment

                    const initialBankKey = validBankEasyPays[0]?.accountKey ?? ''

                    const renewPaymentsStorage = getRenewPaymentsStorage()
                    const {paymentType} = renewPaymentsStorage.get({})

                    const isRenewedAccount =
                        storage.exists() &&
                        storage.get(null).priorityPaymentMethod.payMethod.bankEasyPays.length !==
                            validBankEasyPays.length
                    /**
                     * [D]: 매장방문결제에서 계좌 없음 => 최초 계좌 추가된 경우 다른 n차에서 첫번째 계좌가 선택되어야 함
                     * https://oss.fin.navercorp.com/Product-Planning/Order_QA/issues/8338
                     */
                    const isInitialAccountAdded = validBankEasyPays.length === 1

                    if (isRenewedAccount) {
                        state.payMethod.bankEasyPays = validBankEasyPays
                        state.payMethod.chargedAccounts = validBankEasyPays
                        if (paymentType === PAYMENT_PRIORITY.SECONDARY) {
                            state.selectedSecondPayment.bankEasyPay.accountKey = initialBankKey
                            state.selectedSecondPayment.chargeAccount.accountKey = initialBankKey

                            if (isInitialAccountAdded) {
                                state.selectedPayment.chargeAccount.accountKey = initialBankKey
                            }
                        } else {
                            state.selectedPayment.bankEasyPay.accountKey = initialBankKey
                            state.selectedPayment.chargeAccount.accountKey = initialBankKey

                            if (isInitialAccountAdded) {
                                state.selectedSecondPayment.chargeAccount.accountKey = initialBankKey
                            }
                        }
                    } else {
                        // 계좌 key 유효성검사 필요 > 이전에 결제한 계좌를 삭제한 경우가 있을 수 있음
                        const isPrimaryBankKeyValid = checkObjectWithKeyword(
                            validBankEasyPays,
                            'accountKey',
                            primaryBankKey,
                        )
                        const isRechargeBankKeyValid = checkObjectWithKeyword(
                            validBankEasyPays,
                            'accountKey',
                            chargeKey,
                        )
                        const isSecondaryBankKeyValid = checkObjectWithKeyword(
                            validBankEasyPays,
                            'accountKey',
                            secondaryBankKey,
                        )
                        const isSecondaryRechargeBankKeyValid = checkObjectWithKeyword(
                            validBankEasyPays,
                            'accountKey',
                            secondaryChargeKey,
                        )

                        /** 최근 사용된 계좌키가 있는 경우, 목록 가장 앞에 오도록 정렬 시도 (계좌 목록은 1순위 기준) */
                        state.payMethod.bankEasyPays = shiftToFirstBy<BankEasyPayView>(
                            'accountKey',
                            primaryBankKey,
                        )(validBankEasyPays)
                        state.payMethod.chargedAccounts = shiftToFirstByChargedAccounts(validBankEasyPays, chargeKey)

                        /** 첫번째 전달 계좌로 선택계좌 초기화 */
                        if (isEmpty(primaryBankKey) || !isPrimaryBankKeyValid) {
                            state.selectedPayment.bankEasyPay.accountKey = initialBankKey
                        }
                        if (isEmpty(chargeKey) || !isRechargeBankKeyValid) {
                            state.selectedPayment.chargeAccount.accountKey = initialBankKey
                        }
                        if (isEmpty(secondaryBankKey) || !isSecondaryBankKeyValid) {
                            state.selectedSecondPayment.bankEasyPay.accountKey = initialBankKey
                        }
                        if (isEmpty(secondaryChargeKey) || !isSecondaryRechargeBankKeyValid) {
                            state.selectedSecondPayment.chargeAccount.accountKey = initialBankKey
                        }
                    }
                }

                /** 카드 목록 초기화 */
                const {
                    result: {cardList},
                } = cardResult
                if (cardList) {
                    const nextCardList = cardList.map(convertCardEasyPay())

                    const renewPaymentsStorage = getRenewPaymentsStorage()
                    const {paymentType} = renewPaymentsStorage.get({})

                    const isRenewedCard =
                        storage.exists() &&
                        storage.get(null).priorityPaymentMethod.payMethod.cardEasyPays.length !== nextCardList.length

                    // 카드 등록으로 인한 갱신인 경우, 등록된 카드가 선택되도록 합니다.
                    if (isRenewedCard) {
                        state.payMethod.cardEasyPays = nextCardList
                        const cardId = nextCardList[0]?.cardId ?? ''
                        if (paymentType === PAYMENT_PRIORITY.SECONDARY) {
                            state.selectedSecondPayment.cardEasyPay.cardId = cardId
                        } else {
                            state.selectedPayment.cardEasyPay.cardId = cardId
                        }
                    } else {
                        const {cardId: primaryCardId} = state.selectedPayment.cardEasyPay
                        const {cardId: secondaryCardId} = state.selectedSecondPayment.cardEasyPay
                        const sortedCardList = shiftToFirstBy<CardEasyPay>('cardId', primaryCardId)(nextCardList)
                        state.payMethod.cardEasyPays = sortedCardList

                        // cardId 유효성검사 필요 > 이전에 결제한 카드를 삭제한 경우가 있을 수 있음
                        const isValidCard = (selectedCardId: string) =>
                            nextCardList.some(
                                ({cardId, payServiceAvailable}) => cardId === selectedCardId && payServiceAvailable,
                            )

                        const isValidCardID = isValidCard(primaryCardId)
                        const isSecondaryCardIdValid = isValidCard(secondaryCardId)

                        const firstCardId =
                            (nextCardList.find(({payServiceAvailable}) => payServiceAvailable) ?? nextCardList?.[0])
                                ?.cardId ?? ''

                        const initialCardId = isValidCardID && primaryCardId ? primaryCardId : firstCardId

                        if (isEmpty(primaryCardId) || !isValidCardID) {
                            /** 간편카드 목록 중 첫번째 카드를 선택된 상태로 초기화 */
                            state.selectedPayment.cardEasyPay.cardId = initialCardId
                        }
                        if (isEmpty(secondaryCardId) || !isSecondaryCardIdValid) {
                            state.selectedSecondPayment.cardEasyPay.cardId = initialCardId
                        }
                    }
                }

                state.isLoadFailedEasyAccounts = !isAccountSuccessCode(accountResult.resultCode)
                state.isLoadFailedEasyCards = cardResult.code !== '1000'
            })
            .addCase(restoreOrderSheetDataByStorage.fulfilled, (state, action) => {
                const {selectedPayment, selectedSecondPayment} = action.payload.priorityPaymentMethod ?? state
                const {selectedMethod, prevSelectedMethod, billingPayMethod} = selectedPayment
                const {selectedMethod: selectedSecondaryMethod, billingPayMethod: secondaryBillingPayMethod} =
                    selectedSecondPayment
                const eqSelectedPayment = eq(selectedMethod)

                if (
                    // 새로 조회했는데 계좌목록 조회 실패 & 이전에 선택한 결제수단이 계좌간편 혹은 충전결제인 경우
                    state.isLoadFailedEasyAccounts &&
                    (eqSelectedPayment(SubscribePayMethod.BANK_EASY_PAY) ||
                        eqSelectedPayment(SubscribePayMethod.RECHARGE_POINT))
                ) {
                    return
                } else if (
                    // 새로 조회했는데 카드목록 조회 실패 & 이전에 선택한 결제수단이 카드간편인 경우
                    state.isLoadFailedEasyCards &&
                    eqSelectedPayment(SubscribePayMethod.CARD_EASY_PAY)
                ) {
                    return
                }

                const isInitialAccountAdded =
                    !selectedPayment.chargeAccount.accountKey &&
                    !selectedSecondPayment.chargeAccount.accountKey &&
                    state.selectedPayment.chargeAccount.accountKey

                const {type, paymentType} = action.meta.arg
                const isRenewPayment = type === STORAGE_RECOVERY_TYPE.EXCLUDE_PAYMENTS_ID
                const isSecondaryPaymentRenewed = paymentType === PAYMENT_PRIORITY.SECONDARY

                const storedSelectedPayment =
                    isRenewPayment && !isSecondaryPaymentRenewed
                        ? {
                              selectedMethod,
                              billingPayMethod,
                              prevSelectedMethod,
                          }
                        : {
                              ...selectedPayment,
                              ...(isInitialAccountAdded && {chargeAccount: state.selectedPayment.chargeAccount}),
                          }
                const storedSelectedSecondaryPayment =
                    isRenewPayment && isSecondaryPaymentRenewed
                        ? {
                              selectedMethod: selectedSecondaryMethod,
                              billingPayMethod: secondaryBillingPayMethod,
                          }
                        : {
                              ...selectedSecondPayment,
                              ...(isInitialAccountAdded && {chargeAccount: state.selectedSecondPayment.chargeAccount}),
                          }
                const nextState: PriorityPaymentMethodState = {
                    ...state,
                    ...(action.meta.arg.type === STORAGE_RECOVERY_TYPE.ARS && {
                        ...action.payload.priorityPaymentMethod,
                    }),
                    selectedPayment: {
                        ...state.selectedPayment,
                        ...storedSelectedPayment,
                    },
                    selectedSecondPayment: {
                        ...state.selectedSecondPayment,
                        ...storedSelectedSecondaryPayment,
                    },
                }

                return nextState
            })
            .addCase(updateFirstPayMethod, (state, {payload}) => {
                // 1. 1차 결제수단 선택
                state.selectedPayment.billingPayMethod = convertPaymentTabMethodToBillingPayMethod(payload)
                state.selectedPayment.selectedMethod = convertPaymentTabMethodToPayMethod(payload)
                // 2. 2차 결제수단 첫번째 항목 자동선택
                const firstPaymentMethod = getSecondPaymentMethodByManagementPolicy(payload)[0]
                state.selectedSecondPayment.billingPayMethod = firstPaymentMethod
                    ? convertPaymentTabMethodToBillingPayMethod(firstPaymentMethod)
                    : null
                state.selectedSecondPayment.selectedMethod = firstPaymentMethod
                    ? convertPaymentTabMethodToPayMethod(firstPaymentMethod)
                    : SubscribePayMethod.NONE
            })
            .addCase(updateSecondPayMethod, (state, {payload}) => {
                state.selectedSecondPayment.billingPayMethod = convertPaymentTabMethodToBillingPayMethod(payload)
                state.selectedSecondPayment.selectedMethod = convertPaymentTabMethodToPayMethod(payload)

                /**
                 * 결제수단 변경창의 2순위에는 그 다음으로 우선순위 높은 카드로 설정
                 * https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=153900644
                 */
                const cardList = state.payMethod.cardEasyPays

                if (state.selectedPayment.selectedMethod === SubscribePayMethod.CARD_EASY_PAY && !isEmpty(cardList)) {
                    const firstCardId = state.selectedPayment.cardEasyPay.cardId
                    const firstCardIndex = cardList.findIndex((card) => card.cardId === firstCardId)
                    const isFirstCardHighestPriority = firstCardIndex === 0
                    const nextPriorityCardId = cardList[firstCardIndex + 1]?.cardId ?? ''

                    state.selectedSecondPayment.cardEasyPay.cardId = isFirstCardHighestPriority
                        ? nextPriorityCardId
                        : (cardList[0]?.cardId ?? '')
                }
            })
            .addCase(selectSecondPayMethod, (state, {payload}) => {
                const selectedSecondPayMethod = payload
                state.selectedSecondPayment.billingPayMethod = isBillingPayMethods(selectedSecondPayMethod)
                    ? BillingPayMethodsKey[selectedSecondPayMethod]
                    : null
                state.selectedSecondPayment.selectedMethod = selectedSecondPayMethod
            })
            .addCase(enableRechargePoint, (state, {payload}) => {
                const nextSelectedMethod = payload.selectedPayMethod

                state.selectedPayment.prevSelectedMethod = state.selectedPayment.selectedMethod
                if (payload.rechargePoint.enable) {
                    state.selectedPayment.billingPayMethod = 'recharge'
                    state.selectedPayment.selectedMethod = SubscribePayMethod.RECHARGE_POINT
                } else {
                    state.selectedPayment.billingPayMethod = isBillingPayMethods(nextSelectedMethod)
                        ? BillingPayMethodsKey[nextSelectedMethod]
                        : null
                    state.selectedPayment.selectedMethod = nextSelectedMethod
                }
            })
    },
})

export default slice.reducer
